import { GLRenderer } from "../glrenderer";
import { ViewPortData, ViewPortUpdateFn, IMouseGestureEventHandler, CustomHandler, MouseGestureHandler, ViewPortUpdateReason } from "./MouseGestureUtil";
import {Point, PointUtil, Rectangle} from "../geom";

export class PanZoomHandler extends CustomHandler {
    public constructor(viewPortData: ViewPortData) {
        super();
        this.viewPortData = viewPortData;
    }
    public shouldStartLeftMouseDrag(pt1: Point): boolean {
        return true;
    }
    public shouldStartTouchDrag(pt1: Point): boolean {
        return true;
    }
    public handleClick(pt1: Point): boolean {
        return false;
    }
    public handleTouchSelect(pt1: Point): boolean {
        return false;
    }
    public doMouseDrag(pt1: Point, pt2: Point): boolean {
        if (PointUtil.distance(pt1, pt2) > this.dragPixelsLimit && !this.dragging) {
            this.dragging = true;
            this.orgOrigin = this.viewPortData.origin;
        }
        if (this.dragging) {
            let p = this.viewPortData.clientToLogical(pt1);
            let p2 = this.viewPortData.clientToLogical(pt2);
            this.viewPortData.origin = {x: this.orgOrigin.x + p.x - p2.x, y: this.orgOrigin.y + p.y - p2.y};
            this.viewPortData.updateViewport(ViewPortUpdateReason.Pan);
        }
        return this.dragging;
    }
    public doTouchDrag(pt1: Point, pt2: Point): boolean {
        return this.doMouseDrag(pt1, pt2);
    }
    public doMouseWheel(pt: Point, deltaY: number): boolean {
        let p = this.viewPortData.clientToLogical(pt);
        if (deltaY < 0) {
            this.viewPortData.zoom = this.viewPortData.zoom * 1.1;
        } else if (deltaY > 0) {
            this.viewPortData.zoom = this.viewPortData.zoom / 1.1;
        }
        this.viewPortData.calcViewPort();
        let p2 = this.viewPortData.clientToLogical(pt);
        this.viewPortData.origin = {x: this.viewPortData.origin.x + p.x - p2.x, y: this.viewPortData.origin.y + p.y - p2.y};
        this.viewPortData.updateViewport(ViewPortUpdateReason.Zoom);
        return true;
    }
    public endMouseDrag(): void {
        this.resetState();
    }
    public endTouchDrag(): void {
        this.resetState();
    }
    public shouldStartPinchZoom(pt1: Point, pt2: Point): boolean {
        return true;
    }
    public doPinchZoom(ptOrg1: Point, ptOrg2: Point, ptNew1: Point, ptNew2: Point): boolean {
        if (!this.dragging) {
            this.orgZoom = this.viewPortData.zoom;
        }
        this.dragging = true;
        let zoomScale = PointUtil.distance(ptNew1, ptNew2) / PointUtil.distance(ptOrg1, ptOrg2);
        this.viewPortData.zoom = this.orgZoom * zoomScale;
        this.viewPortData.updateViewport(ViewPortUpdateReason.Zoom);
        return this.dragging;
    }
    public endPinchZoom(): void {
        this.resetState();
    }
    public resetState(): void {
        this.dragging = false;
        this.orgZoom = 1;
    }
    public isDragging(): boolean {
        return this.dragging;
    }
    protected dragging: boolean = false;
    protected dragPixelsLimit: number = 10;
    protected orgOrigin = {x: 0, y: 0};
    protected orgZoom: number = 1;

    protected viewPortData: ViewPortData;
}

/**
 * Default mouse/touch handler with pan/zoom
 */
class PanZoomMouseGestureHandler implements IMouseGestureEventHandler {
    /**
     * 
     * @param viewPortUpdate callback that gets called when viewPort should be changed due to mouse/touch events.
     */
   public constructor(viewPortUpdate?: ViewPortUpdateFn) {
       this.viewPortData = new ViewPortData(viewPortUpdate);
       this.panHandler = new PanZoomHandler(this.viewPortData);
       this.mouseGestureHandler = new MouseGestureHandler();
       this.mouseGestureHandler.handlers.push(this.panHandler);
   }
   public setViewPortUpdateFn(viewPortUpdate?: ViewPortUpdateFn) {
       this.viewPortData.setViewPortUpdateFn(viewPortUpdate);
   }
   public registerEvents(canvas: HTMLElement, renderer: GLRenderer, enableResizeObserver=true): () => void {
        this.canvasSizeChanged(canvas.clientWidth, canvas.clientHeight);
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
        }
        var handler = this;
        if (!this.resizeObserver && enableResizeObserver) {
           if (typeof ResizeObserver !== 'undefined') {
              this.resizeObserver = new ResizeObserver(
                 entries => {
                    if (renderer) {
                       renderer.handleCanvasSizeChanged();
                    }
                    handler.canvasSizeChanged(canvas.clientWidth, canvas.clientHeight);
                 }
              );
              this.resizeObserver.observe(canvas);
           }
        }
        canvas.onmousedown = function(ev: MouseEvent) {
            handler.mouseDown(ev);
        }
        canvas.onmousemove = function(ev: MouseEvent) {
            handler.mouseMove(ev);
        }
        canvas.onmouseup = function(ev: MouseEvent) {
            handler.mouseUp(ev);
        }
        canvas.ontouchstart = function(ev: TouchEvent) {
            handler.touchStart(ev);
        }
        canvas.ontouchend = function(ev: TouchEvent) {
            handler.touchEnd(ev);
        }
        canvas.ontouchmove = function(ev: TouchEvent) {
            handler.touchMove(ev);
        }
        canvas.onwheel = function(ev: WheelEvent) {
            handler.mouseWheel(ev);
        }
        canvas.onmouseenter = function(ev: MouseEvent) {
            handler.mouseEnter(ev);
        }
        canvas.onmouseleave = function(ev: MouseEvent) {
             handler.mouseLeave(ev);
        }
        return () => {
            canvas.onmousedown = null
            canvas.onmousemove = null
            canvas.onmouseup = null
            canvas.ontouchstart = null
            canvas.ontouchend = null
            canvas.ontouchmove = null
            canvas.onwheel = null
            canvas.onmouseenter = null
            canvas.onmouseleave = null
        }
   }
   public mouseDown(mouseEvent: MouseEvent): void {
       this.mouseGestureHandler.mouseDown(mouseEvent);
   }
   public mouseUp(mouseEvent: MouseEvent): void {
       this.mouseGestureHandler.mouseUp(mouseEvent);
   }
   public mouseMove(mouseEvent: MouseEvent): void {
       this.mouseGestureHandler.mouseMove(mouseEvent);
   }
   public mouseWheel(wheelEvent: WheelEvent): void {
       this.mouseGestureHandler.mouseWheel(wheelEvent);
   }
   public touchStart(touchEvent: TouchEvent): void {
       this.mouseGestureHandler.touchStart(touchEvent);
   }
   public touchEnd(touchEvent: TouchEvent): void {
       this.mouseGestureHandler.touchEnd(touchEvent);
   }
   public touchMove(touchEvent: TouchEvent): void {
       this.mouseGestureHandler.touchMove(touchEvent);
   }
   public mouseEnter(mouseEvent: MouseEvent): void {
       this.mouseGestureHandler.mouseEnter(mouseEvent);
   }
   public mouseLeave(mouseEvent: MouseEvent): void {
       this.mouseGestureHandler.mouseLeave(mouseEvent);
   }
    public canvasSizeChanged(cx: number, cy:number): void {
        this.viewPortData.canvasSizeChanged(cx, cy);
    }
   public isDragging(): boolean {
       return this.mouseGestureHandler.isDragging();
   }
   public getViewPortData(): ViewPortData {
       return this.viewPortData;
   }
   public setViewPort(viewPort: Rectangle) {
       this.viewPortData.setViewport(viewPort);
   }
   protected viewPortData: ViewPortData;
   protected panHandler: PanZoomHandler;
   protected mouseGestureHandler: MouseGestureHandler;
   protected resizeObserver: ResizeObserver | null = null;
}

export default PanZoomMouseGestureHandler;